import { ref, watch, computed } from 'vue';
import type { Ref } from 'vue';

import Utils from '@/api/Utils';
import MapConsts from '@/components/map/consts/MapConsts';
import type { QualityColorMode } from '@/components/map/consts/MapConsts';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';

import {
	addBaseStationStatusData,
	addCoords,
	withCoords,
	addGetterData,
	getStatusVisualProps,
	parseConnectedBaseStation,
} from '@/utils/baseStationsUtils';

import {
	useBaseStationsStore,
	SUB_NETWORK_OPTIONAL_MODES,
	SUB_NETWORK_STRICT_MODES,
} from '@/stores/baseStations';
import type {
	BaselineStatus,
	BaselineStatusOnline,
	BaselineWithCoords,
	BaseStationFeatureItem,
	BaseStationStyleFeatureData,
	CoverageContourFeature,
	GridpointDisplay,
	LonLat,
	MapMode,
	ProfileFeatureItem,
	QualityRectangleFeature,
	RoverHistoryCurrentDisplay,
	RoverHistoryFragmentDisplay,
	VRSParticipantRoverDisplay,
} from '@/components/map/types/mapFeatureTypes';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useUserStore } from '@/stores/user';
import { useGnssVisibilityStore, useGnssPerGridpointStore } from '@/stores/gnssVisibilityStore';
import { factory, useSnrStore, useMultipathStore, useResidualsStore } from '@/stores/snrMultipathStore';
import { usePaasStddevStore } from '@/stores/paasStddevStore';
import { useMapGssOverlayStore } from '@/stores/mapGssOverlayStore';
import { useBaselinesStore } from '@/stores/baselinesStore';
import { useRoversHistoryStore } from '@/stores/roversHistoryStore';
import {
	getStationSolutionState,
	getSubnetworkSummary,
} from '@/components/map/tooltips/vrsAccuracyMetricsUtils';
import type { SubNetworkSummary } from '@/components/map/tooltips/vrsAccuracyMetricsUtils';

import type {
	BaseStationOnlineStatusKey,
	BaseStationStatusKey,
	CoordsBLItem,
	ServiceKey,
	VRSParticipantRoverRaw,
} from '@/utils/types/baseStationsTypes';
import type { MapBaseStationStored, SubNetworkRaw } from '@/api/ApiTypes';

import { asRoversHistoryStore } from '@/stores/types/mapStoreContracts';
import type {
	BaselineStoreItem,
	GnssTooltipDisplayItem,
	GnssCode,
	MapModeStoreContract,
	MetricDbItem,
	MetricDbItems,
	MetricFactoryStoreContract,
	Mountpoint,
	RoverHistoryCurrentSelected,
	RoverHistoryFragmentRaw,
} from '@/stores/types/mapStoreContracts';


const isBaselineMapStatus = (
	status: BaseStationStatusKey,
): status is BaselineStatus => (
	status === 'working' || status === 'stopped' || status === 'restarting'
);


const isBaselineMapStatusOnline = (
	statusOnline: BaseStationOnlineStatusKey,
): statusOnline is BaselineStatusOnline => (
	statusOnline !== 'some_breaks'
	&& statusOnline !== 'offlineInLegend'
	&& statusOnline !== 'unknown'
);


const toBaselineWithCoords = (
	baseline: BaselineStoreItem,
	base: LonLat & { id: number },
	rover: LonLat & { id: number },
): BaselineWithCoords => ({
	id: baseline.id,
	status: isBaselineMapStatus(baseline.status) ? baseline.status : 'working',
	status_online: isBaselineMapStatusOnline(baseline.status_online)
		? baseline.status_online
		: 'no_data',
	updating: baseline.updating,
	deleted: baseline.deleted,
	base,
	rover,
});


// 'all' плюс конкретный сервис базовой станции/ровера.
type BsFilterSelected = 'all' | ServiceKey;

type MapBaseStationWithCoords = MapBaseStationStored & {
	b_coord: number;
	l_coord: number;
};

type SubNetworksGeojson = {
	coverage: CoverageContourFeature[][];
	quality: QualityRectangleFeature[][];
};


// D1: mntpnt_int опционален (удаляется для анонимов) — читаем optional-поля через guard.
const hasMapCoords = <T extends CoordsBLItem>(
	item: T,
): item is T & { b_coord: number; l_coord: number } => {
	return item.b_coord != null && item.l_coord != null;
};


const includesInVrs = (item: MapBaseStationStored): boolean => (
	'include_in_vrs' in item && item.include_in_vrs
);


const includesInNear = (item: MapBaseStationStored): boolean => (
	'include_in_near' in item && item.include_in_near
);


// D2: единый typed-парсер coverage/quality JSON-строк (безопасен к null/'').
const parseGeojsonString = <T>(value: string): T[] => {
	if (!value) {
		return [];
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(value);
	} catch {
		return [];
	}
	return Array.isArray(parsed) ? parsed : [];
};


const createFeatureData = (
	mode: MapMode,
	item: MapBaseStationStored,
	gnssSelected: GnssCode | null,
): BaseStationStyleFeatureData => {
	const result: BaseStationStyleFeatureData = {
		...addBaseStationStatusData(addCoords(item)),
	};
	const store: MetricFactoryStoreContract | null = factory(mode);
	// factory != null только для snr/multipath/residuals (никогда paasStddev),
	// исключение paasStddev из mode не даёт конфликта с типизированным полем result.paasStddev
	if (store != null && mode !== MAP_MODE.paasStddev) {
		// D1: guard mntpnt_int перед чтением Map по ключу
		const mountpoint = item.mntpnt_int;
		const dbItem = mountpoint != null ? store.dbItems.get(mountpoint) : null;
		const metric = (dbItem != null && gnssSelected != null) ? (dbItem[gnssSelected] ?? null) : null;
		if (mode === MAP_MODE.snr) {
			result.snr = metric;
		} else if (mode === MAP_MODE.multipath) {
			result.multipath = metric;
		} else if (mode === MAP_MODE.residuals) {
			result.residuals = metric;
		}
	}
	if (mode === MAP_MODE.paasStddev) {
		const stddevStore = usePaasStddevStore();
		// D1: guard mntpnt_int перед чтением Map по ключу
		const mountpoint = item.mntpnt_int;
		const stddevValue = mountpoint != null ? stddevStore.items.get(mountpoint) : null;
		result.paasStddev = stddevValue ?? null;
	}
	if (mode === MAP_MODE.main) {
		const overlayStore = useMapGssOverlayStore();
		const mountpoint = item.mntpnt_int;
		result.gssFailingGnss = mountpoint != null
			? (overlayStore.failingGnssByMountpoint.get(mountpoint) ?? [])
			: [];
	}
	return result;
};


const getBSFilter = (mode: MapMode, bsFilterSelected: BsFilterSelected) => (item: MapBaseStationStored): boolean => {
	if (!withCoords(item)) {
		return false;
	}
	if (mode === MAP_MODE.residuals) {
		return includesInVrs(item) && item.subNetworkIds.length > 0;
	}
	if (mode === MAP_MODE.main && bsFilterSelected === 'vrs') {
		return item.subNetworkIds.length > 0;
	}
	return true;
};


const isSubNetworkOptionalFilterEnabled = (mode: MapMode): boolean => (
	// режимы, где есть фильтр по подсети + пункт Все подсети
	SUB_NETWORK_OPTIONAL_MODES.includes(mode)
);


const isStrictSubNetworkFilterEnabled = (mode: MapMode): boolean => (
	// режимы, где есть обязательный фильтр по подсети
	isSubNetworkOptionalFilterEnabled(mode) || SUB_NETWORK_STRICT_MODES.includes(mode)
);


const getSubNetworkFilter = (
	mode: MapMode,
	subNetworkFilterSelected: number,
	baseStationsInSN: number[] | null,
) => (item: MapBaseStationStored): boolean => {
	if (subNetworkFilterSelected === 0 && isSubNetworkOptionalFilterEnabled(mode)) {
		return true;
	}
	if (baseStationsInSN === null || !isStrictSubNetworkFilterEnabled(mode)) {
		return true;
	}
	return baseStationsInSN.includes(item.id);
};


const getRoverSubNetworkFilter = (
	mode: MapMode,
	subNetworkFilterSelected: number,
) => (item: VRSParticipantRoverRaw): boolean => {
	if (subNetworkFilterSelected === 0 || !isStrictSubNetworkFilterEnabled(mode)) {
		return true;
	}
	return subNetworkFilterSelected === item.sub_network_id;
};


const modifyStatus = (
	mode: MapMode,
	bsFilterSelected: BsFilterSelected,
) => (item: MapBaseStationWithCoords): MapBaseStationWithCoords => {
	if (
		(mode === MAP_MODE.main && bsFilterSelected === 'vrs' && !includesInVrs(item)) ||
		(mode === MAP_MODE.main && bsFilterSelected === 'near' && !includesInNear(item)) ||
		(mode === MAP_MODE.subNetworks && !includesInVrs(item))
	) {
		return {
			...item,
			// визуально перекрашиваем в черный цвет
			updating: false,
			status: 'stopped',
		};
	}
	return item;
};


const addBSFeatureData = (
	mode: MapMode,
	gnssSelected: GnssCode | null,
	dbItems: MetricDbItems,
	allSubnetworksMode: boolean,
) => (item: MapBaseStationWithCoords): BaseStationFeatureItem => {
	const featureData = createFeatureData(mode, item, gnssSelected);
	if (mode === MAP_MODE.coverage) {
		// это точно надо. Он задает цвет кружочка на карте
		featureData.vrsAccuracyData = getStatusVisualProps(getStationSolutionState(featureData, dbItems, allSubnetworksMode));
	}
	return {
		featureData,
		lat: item.b_coord,
		lon: item.l_coord,
		radius: item.radius,
	};
};


const getRoverFilter = (mode: MapMode, bsFilterSelected: BsFilterSelected) => (item: VRSParticipantRoverRaw): boolean => {
	if (mode === MAP_MODE.main) {
		return bsFilterSelected === 'all' || bsFilterSelected === item.service;
	}
	return true;
};


const omitConnectedBaseStation = (
	{ connected_base_station, ...rest }: VRSParticipantRoverRaw,
): Omit<VRSParticipantRoverRaw, 'connected_base_station'> => {
	void connected_base_station;
	return rest;
};


// D3: connectedBaseStation парсится один раз; featureData получает тот же объект.
const addRoverFeatureData = (item: VRSParticipantRoverRaw): VRSParticipantRoverDisplay => {
	const connectedBaseStation = parseConnectedBaseStation(item.connected_base_station);
	return {
		...omitConnectedBaseStation(item),
		lat: item.b_coord,
		lon: item.l_coord,
		connectedBaseStation,
		featureData: addGetterData(item, connectedBaseStation),
	};
};


const addRoverHistoryCurrentFeatureData = (item: RoverHistoryCurrentSelected): RoverHistoryCurrentDisplay => ({
	...item,
	lat: item.b_coord,
	lon: item.l_coord,
});


const addRoverHistoryFragmentFeatureData = (item: RoverHistoryFragmentRaw): RoverHistoryFragmentDisplay => ({
	...item,
	lat1: item.b_coord,
	lon1: item.l_coord,
	lat2: item.b_coord2,
	lon2: item.l_coord2,
});


const MAP_HOVER_POPUP_MODES: ReadonlySet<MapMode> = new Set([
	MAP_MODE.main,
	MAP_MODE.roversHistory,
	MAP_MODE.coverage,
]);


const GNSS_TOOLTIP_SUBNETWORK_MODES: ReadonlySet<MapMode> = new Set([
	MAP_MODE.residuals,
	MAP_MODE.coverage,
]);


export function useMapData(
	gnssSelectedRef: Ref<string | null>,
	bsFilterSelectedRef: Ref<BsFilterSelected>,
	subNetworkFilterSelectedRef: Ref<number>,
	baseStationsInSNRef: Ref<number[] | null>,
) {
	const baseStationsStore = useBaseStationsStore();
	const subNetworksStore = useSubNetworksStore();
	const allSubnetworksMode = computed((): boolean => subNetworkFilterSelectedRef.value === 0);
	const isCoverageModeWithResidualsDetails = computed((): boolean => (
		baseStationsStore.mode === MAP_MODE.coverage && !allSubnetworksMode.value
	));
	const userStore = useUserStore();
	const gnssPerGridpointStore: MapModeStoreContract = useGnssPerGridpointStore();
	const residualsStore: MapModeStoreContract = useResidualsStore();
	const STORES: Partial<Record<MapMode, MapModeStoreContract>> = {
		[MAP_MODE.gnss]: useGnssVisibilityStore(),
		[MAP_MODE.gnssPerGridpoint]: gnssPerGridpointStore,
		[MAP_MODE.snr]: useSnrStore(),
		[MAP_MODE.multipath]: useMultipathStore(),
		[MAP_MODE.residuals]: residualsStore,
		[MAP_MODE.coverage]: residualsStore,
	};

	const baseStations = computed((): BaseStationFeatureItem[] => {
		const filterFunc0 = getBSFilter(baseStationsStore.mode, bsFilterSelectedRef.value);
		const filterFunc1 = getSubNetworkFilter(baseStationsStore.mode, subNetworkFilterSelectedRef.value, baseStationsInSNRef.value);
		const mapFunc0 = modifyStatus(baseStationsStore.mode, bsFilterSelectedRef.value);
		const mapFunc1 = addBSFeatureData(
			baseStationsStore.mode,
			gnssSelectedRef.value,
			isCoverageModeWithResidualsDetails.value
				? residualsStore.dbItems
				: new Map<Mountpoint, MetricDbItem>(),
			allSubnetworksMode.value,
		);
		return baseStationsStore.items
			.filter(filterFunc0)
			.filter(filterFunc1)
			.filter(hasMapCoords)
			.map(mapFunc0)
			.map(mapFunc1);
	});

	const vrsAccuracySummary = computed((): SubNetworkSummary | null => {
		if (!isCoverageModeWithResidualsDetails.value) {
			return null;
		}
		const subNetwork = subNetworksStore.getSubNetworkById(subNetworkFilterSelectedRef.value);
		if (subNetwork == null) {
			return null;
		}
		return getSubnetworkSummary(
			subNetwork,
			baseStationsStore.items,
			residualsStore.dbItems,
		);
	});

	const rovers = computed((): VRSParticipantRoverDisplay[] => {
		const filterFunc0 = getRoverFilter(baseStationsStore.mode, bsFilterSelectedRef.value);
		const filterFunc1 = getRoverSubNetworkFilter(baseStationsStore.mode, subNetworkFilterSelectedRef.value);
		return baseStationsStore.rovers.filter(filterFunc0).filter(filterFunc1).map(addRoverFeatureData);
	});
	const roversActive = computed((): VRSParticipantRoverDisplay[] => rovers.value.filter(item => item.status !== 'failed'));
	const roversFailed = computed((): VRSParticipantRoverDisplay[] => rovers.value.filter(item => item.status === 'failed'));

	// # TODO gridpoints done: здесь появляется новый уровень вложенности. SN -> c-net_num -> gridpoints
	const gridpoints = computed((): GridpointDisplay[] => {
		if (subNetworksStore.gridpoints == null) {
			return [];
		}
		return subNetworksStore.gridpoints.map(item => ({
			...item,
			featureData: {
				...item,
				id: item.label,
				mntpnt_int: item.label,
			},
			lat: Utils.intToFloatScale(item.latitude),
			lon: Utils.intToFloatScale(item.longitude),
		}));
	});

	const showGridpointsOnly = computed({
		get: (): boolean => !gnssPerGridpointStore.needsVisibilityData,
		set: (value: boolean) => {
			gnssPerGridpointStore.needsVisibilityData = !value;
		},
	});
	const measureEnabled = ref(false);
	const fillCoverageArea = ref(true);
	const showBaseStationTooltips = ref(true);
	const qualityColorMode = ref<QualityColorMode>(MapConsts.QUALITY_COLOR_MODE.vrsPst);
	const mapClickEnabled = computed((): boolean => (
		!measureEnabled.value &&
		userStore.isBaseStationChangeAllowed &&
		baseStationsStore.mode !== MAP_MODE.gnssPerGridpoint &&
		baseStationsStore.mode !== MAP_MODE.roversHistory
	));
	const mapHoverPopupEnabled = computed((): boolean => (
		MAP_HOVER_POPUP_MODES.has(baseStationsStore.mode) && !measureEnabled.value
	));
	watch(
		() => baseStationsStore.mode,
		() => {
			measureEnabled.value = false;
			showGridpointsOnly.value = false;
		},
	);
	const selectedStore = computed((): MapModeStoreContract | undefined => STORES[baseStationsStore.mode]);
	const gnssTooltipsOverlayDisplayItems = computed((): GnssTooltipDisplayItem[] => {
		const store = selectedStore.value;
		if (store == null) {
			return [];
		}
		if (baseStationsStore.mode === MAP_MODE.coverage && !showBaseStationTooltips.value) {
			return [];
		}
		if (baseStationsStore.mode === MAP_MODE.coverage && allSubnetworksMode.value) {
			return [];
		}
		if (GNSS_TOOLTIP_SUBNETWORK_MODES.has(baseStationsStore.mode)) {
			const subNetwork = subNetworksStore.getSubNetworkById(subNetworksStore.subNetworkSelectedId);
			if (subNetwork !== null) {
				return store.displayItems.filter(displayItem =>
					subNetwork.base_stations.some(bs => bs.id === displayItem.featureData.id)
				);
			}
		}
		return store.displayItems;
	});
	const gnssTooltipsNonNullGnss = computed((): string[] => (
		selectedStore.value != null ? selectedStore.value.nonNullGnss : []
	));

	const baselinesStore = useBaselinesStore();
	const baselines = computed((): BaselineWithCoords[] => {
		const bsCoords = new Map<number, LonLat & { id: number }>();
		for (const item of baseStationsStore.items) {
			if (item.b_coord != null && item.l_coord != null) {
				bsCoords.set(item.id, {
					id: item.id,
					lat: item.b_coord,
					lon: item.l_coord,
				});
			}
		}
		const baselinesWithCoords: BaselineWithCoords[] = [];
		for (const baseline of baselinesStore.items) {
			const base = bsCoords.get(baseline.base.id);
			const rover = bsCoords.get(baseline.rover.id);
			if (base != null && rover != null) {
				baselinesWithCoords.push(toBaselineWithCoords(baseline, base, rover));
			}
		}
		return baselinesWithCoords;
	});

	const profiles = computed((): ProfileFeatureItem[] => (
		baseStationsStore.profiles.flatMap(profile => {
			if (profile.lat == null || profile.lon == null) {
				return [];
			}
			return [{
				username: profile.username,
				lat: profile.lat,
				lon: profile.lon,
				radius: profile.radius,
			}];
		})
	));

	const subNetworksGeojson = computed((): SubNetworksGeojson => {
		const sourceSubNetworks = subNetworkFilterSelectedRef.value !== 0
			? [subNetworksStore.getSubNetworkById(subNetworkFilterSelectedRef.value)].filter((item): item is SubNetworkRaw => item != null)
			: subNetworksStore.subNetworks;
		return {
			coverage: sourceSubNetworks
				.map(item => item.coverage)
				.filter((value): value is string => value != null)
				.map(value => parseGeojsonString<CoverageContourFeature>(value)),
			quality: sourceSubNetworks
				.map(item => item.quality)
				.filter((value): value is string => value != null)
				.map(value => parseGeojsonString<QualityRectangleFeature>(value)),
		};
	});

	const roversHistoryStore = asRoversHistoryStore(useRoversHistoryStore());
	const roversHistoryAutoUpdate = computed((): boolean => roversHistoryStore.autoUpdate);
	const currentRoversSelectedFeatures = computed((): RoverHistoryCurrentDisplay[] => (
		(roversHistoryStore.showOnMap && roversHistoryAutoUpdate.value)
			? roversHistoryStore.currentRoversSelected.map(addRoverHistoryCurrentFeatureData)
			: []
	));
	const roversHistoryPaths = computed((): RoverHistoryFragmentDisplay[][] => (
		roversHistoryStore.showOnMap
			? roversHistoryStore.roverPaths.map(item => item.path.map(addRoverHistoryFragmentFeatureData))
			: []
	));

	return {
		featureData: {
			baseStations,
			rovers,
			roversActive,
			roversFailed,
			gridpoints,
			profiles,
			subNetworksGeojson,
			baselines,
			currentRoversSelectedFeatures,
			roversHistoryPaths,
		},
		displayOptions: {
			showGridpointsOnly,
			measureEnabled,
			fillCoverageArea,
			showBaseStationTooltips,
			qualityColorMode,
			roversHistoryAutoUpdate,
			allSubnetworksMode,
		},
		interaction: {
			mapClickEnabled,
			mapHoverPopupEnabled,
		},
		tooltips: {
			gnssTooltipsOverlayDisplayItems,
			gnssTooltipsNonNullGnss,
			vrsAccuracySummary,
		},
	};
}


export type UseMapDataReturn = ReturnType<typeof useMapData>;
export type MapFeatureData = UseMapDataReturn['featureData'];
export type MapDisplayOptions = UseMapDataReturn['displayOptions'];
